import { cdnUrl } from "@/consts";
import { Show, TextField } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { Typography, Card, Descriptions, Table, Row, Col, Tag } from "antd";
import { formatDistanceToNow } from "date-fns";
import { Key, ReactElement, JSXElementConstructor, ReactNode, ReactPortal } from "react";

const { Title, Text } = Typography;

const formatDate = (dateString?: string) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return "Invalid date";
  }
};

const formatDuration = (minutes?: number) => {
  if (!minutes) return "N/A";
  if (minutes < 60) return `${minutes} minutes`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)} hours`;
  return `${Math.floor(minutes / 1440)} days`;
};

export const MissionShow = () => {
  const { query } = useShow();
  const { data, isLoading } = query;
  const mission = data?.data.data;

  return (
    <Show isLoading={isLoading}>
      <Card>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <Title level={3}>{mission?.name}</Title>
              <Tag color="blue">{mission?.missionType?.name}</Tag>
            </div>
          </Col>

          <Col span={12}>
            <Card title="Basic Information" bordered={false}>
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Description">
                  <TextField value={mission?.description || "N/A"} />
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  <TextField value={mission?.missionType?.name} />
                  {mission?.missionType?.description && (
                    <Text type="secondary" style={{ display: "block" }}>
                      {mission.missionType.description}
                    </Text>
                  )}
                </Descriptions.Item>
              
                {mission?.missionRound && (
                  <Descriptions.Item label="Rounds">
                    <TextField value={mission?.missionRound?.rounds} />
                  </Descriptions.Item>
                )}
                {mission?.missionTarget.missionMode && (
                  <Descriptions.Item label="Mode">
                    <TextField value={mission?.missionTarget.missionMode} />
                  </Descriptions.Item>
                )}
                {mission?.missionTarget && (
                  <Descriptions.Item label="Target">
                    <div>
                      <TextField
                        value={`${mission.missionTarget.targetUnit.actionVerb} ${mission.missionTarget.targetValue} ${mission.missionTarget.targetUnit.name}`}
                      />
                      {mission.missionTarget.targetUnit.description && (
                        <Text type="secondary" style={{ display: "block" }}>
                          {mission.missionTarget.targetUnit.description}
                        </Text>
                      )}
                    </div>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Featured">
                  <TextField value={mission?.isFeatured ? "Yes" : "No"} />
                </Descriptions.Item>
                <Descriptions.Item label="Ends At">
                  <TextField
                    value={
                      mission?.endsAt
                        ? formatDate(mission.endsAt)
                        : "N/A"
                    }
                  />
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Timestamps" bordered={false}>
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Created At">
                  <TextField
                    value={formatDate(mission?.createdAt)}
                    title={
                      mission?.createdAt
                        ? new Date(mission.createdAt).toLocaleString()
                        : "N/A"
                    }
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Updated At">
                  <TextField
                    value={formatDate(mission?.updatedAt)}
                    title={
                      mission?.updatedAt
                        ? new Date(mission.updatedAt).toLocaleString()
                        : "N/A"
                    }
                  />
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {mission?.missionRound ? (
            <Col span={24}>
              <Card title="Workout Details" bordered={false}>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5} style={{ marginBottom: 8 }}>
                      Workouts
                    </Title>
                    <Text type="secondary">
                      Repeat {mission?.missionRound?.rounds} times
                    </Text>
                  </div>
                  <Table
                    dataSource={mission?.missionRound?.workouts}
                    rowKey={(record) => `${record.workoutID}-${record.index}`}
                    pagination={false}
                  >
                    <Table.Column
                      title="Workout"
                      dataIndex={["workout", "name"]}
                      render={(text, record: any) => (
                        <div>
                          <Text strong>{text}</Text>
                          {record.workout.description && (
                            <Text type="secondary" style={{ display: "block" }}>
                              {record.workout.description}
                            </Text>
                          )}
                        </div>
                      )}
                    />
                    <Table.Column
                      title="Reps"
                      dataIndex="reps"
                      render={(reps) => `${reps} reps`}
                    />
                  </Table>
                </div>
              </Card>
            </Col>
          ) : (
            mission?.missionTarget && <></>
          )}

          {/* Media Display Section */}
          {mission?.attachments && mission.attachments.length > 0 && (
            <Col span={24}>
              <Card title="Media" bordered={false}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {mission.attachments
                    .filter((a: any) => a.fileUpload && a.fileUpload.id)
                    .map((a: any) => ({
                      id: a.fileUpload.id,
                      type: a.fileUpload.fileType || "image",
                      name: a.fileUpload.fileName || "",
                    }))
                    .map((media: { id: Key | null | undefined; type: string; name: string | number | bigint | boolean | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | Promise<string | number | bigint | boolean | ReactPortal | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | null | undefined> | null | undefined; }) => (
                      <div
                        key={media.id}
                        style={{
                          border: "1px solid #5c5a5aff",
                          borderRadius: 6,
                          padding: 8,
                          width: 180,
                        }}
                      >
                        {media.type.startsWith("image") ||
                        media.type === "IMAGE" ? (
                          <img
                            src={`${cdnUrl}/${media.id}`}
                            alt={media.name as string}
                            style={{
                              width: "100%",
                              maxHeight: 120,
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <video
                            src={`${cdnUrl}/${media.id}`}
                            controls
                            style={{ width: "100%", maxHeight: 120 }}
                          />
                        )}
                        <div style={{ marginTop: 8, fontSize: 12 }}>
                          {String(media.name)}
                        </div>
                      </div>
                    ))}
                </div>
              </Card>
            </Col>
          )}
        </Row>
      </Card>
    </Show>
  );
};
